import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useState } from 'react';
import AppConfig from '~/config/AppConfig';
import useQueryAgent, { setQueryBaseURLs } from './hooks/useQueryAgent';
import UserService from './auth/UserService';
import AccessDeniedHeader from './components/AccessDeniedHeader';
import AccessDenied from './components/AccessDenied';

const { VITE_API_BASE_URL, VITE_DATA_DICTIONARY_BASE_URL } = import.meta.env;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      retryOnMount: false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      structuralSharing: false,
      staleTime: 0,
      gcTime: 0,
    },
  },
});

const AppProvider = createContext({});

// eslint-disable-next-line react-refresh/only-export-components
export function useAppContext() {
  return useContext(AppProvider);
}

export default function AppProviders({ children }) {
  const [isRealmLoading, setIsRealmLoading] = useState(true);
  const { getWorkspace } = useQueryAgent();
  const [showAccessDeniedPage, setShowAccessDeniedPage] = useState(false);
  const [baseAPIConfig, setBaseAPIConfig] = useState({
    userName: '',
    accountName: '',
    realmName: '',
    workspaceId: '',
  });

  const fetchWorkspaceAPI = async () => {
    try {
      setIsRealmLoading(true);
      const { data, headers } = await getWorkspace();
      if (!data) {
        setIsRealmLoading(false);
        return;
      }

      const realmName = headers?.['header-link']?.split('/')?.pop();
      if (!realmName) throw new Error('Realm name not found in response headers');
      sessionStorage.setItem('realm_name', realmName);
      sessionStorage.setItem('currency', data?.workspace?.currency);

      const baseDemandAPI = VITE_API_BASE_URL.replace('REALM_NAME', realmName);
      const baseDataDictionaryAPI = VITE_DATA_DICTIONARY_BASE_URL.replace('REALM_NAME', realmName);

      setQueryBaseURLs({ baseDemandAPI, baseDataDictionaryAPI });

      setBaseAPIConfig(prev => ({
        ...prev,
        userName: data?.user?.name ?? 'User',
        accountName: data?.workspace?.name,
        workspaceId: data?.workspace?.id,
        realmName,
      }));
      setIsRealmLoading(false);
    } catch (error) {
      console.error('Failed to fetch workspace:', error);
      if (error?.response?.status === 401) UserService.doLogout();
      if (error?.response?.status === 404 || error?.response?.status === 403)
        setShowAccessDeniedPage(true);
      setIsRealmLoading(false);
      // eslint-disable-next-line preserve-caught-error
      throw new Error('Workspace API request failed');
    }
  };

  useEffect(() => {
    (async () => {
      await fetchWorkspaceAPI();
    })();
  }, []);

  const { snackbarAutoHideDuration } = AppConfig;

  if (showAccessDeniedPage) {
    return (
      <>
        <AccessDeniedHeader />
        <AccessDenied />
      </>
    );
  }

  const AppProviderValues = {
    logoutAppSession: UserService.doLogout,
    keycloak: UserService.getKeycloakConfig(),
    token: UserService.getToken(),
    isRealmLoading,
    realmName: baseAPIConfig.realmName,
    ...baseAPIConfig,
  };

  // Return JSX
  return (
    <AppProvider.Provider value={AppProviderValues}>
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider
          maxSnack={3}
          autoHideDuration={snackbarAutoHideDuration}
          disableWindowBlurListener={true}>
          {children}
        </SnackbarProvider>
        {/* <ReactQueryDevtools /> */}
      </QueryClientProvider>
    </AppProvider.Provider>
  );
}

// Write PropTypes for AppProviders
AppProviders.propTypes = {
  children: PropTypes.node.isRequired,
};
